/* eslint-disable no-console */
import React from 'react';
import PropTypes from 'prop-types';
import { uniqueId } from 'lodash/fp';
import classnames from 'classnames';

import RenderInBody from 'components/shared/render_in_body';
import Dismissable from 'components/shared/dismissable';
import Icon from '../Icon';
import {
  focusLastFocusableNode,
  focusFirstFocusableNode,
} from '../utilities/focus';

import ModalContext from './ModalContext';

class Modal extends React.Component {
  modalChildrenRef = React.createRef();

  componentDidMount() {
    if (this.props.shown) {
      document.body.classList.add('no-scroll');
    }
    window.addEventListener('keydown', this.handleKeyDown);
    setTimeout(() => {
      if (this.modalChildrenRef.current && !this.props.manualAutoFocus) {
        focusFirstFocusableNode(this.modalChildrenRef.current);
      }
    }, 0);
  }

  componentDidUpdate() {
    if (this.props.shown) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    setTimeout(() => {
      if (this.modalChildrenRef.current && !this.props.manualAutoFocus) {
        focusFirstFocusableNode(this.modalChildrenRef.current);
      }
    }, 0);
  }

  componentWillUnmount() {
    document.body.classList.remove('no-scroll');
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  handleFocusWrap = first => () => {
    if (this.modalChildrenRef.current) {
      if (first) {
        focusLastFocusableNode(this.modalChildrenRef.current);
      } else {
        focusFirstFocusableNode(this.modalChildrenRef.current);
      }
    }
  };

  dismissModal = e => {
    const { onDismiss, activatorRef } = this.props;
    onDismiss(e);
    if (activatorRef && activatorRef.current) {
      activatorRef.current.focus();
    }
  };

  handleKeyDown = e => {
    const keyCode = e.keyCode || e.which;
    const { shown, enableFloatingDismiss } = this.props;
    if (shown && enableFloatingDismiss && keyCode === 27) {
      this.dismissModal(e);
    }
  };

  render() {
    const { children, shown, className } = this.props;
    const id = uniqueId('modal_');
    const modalHeaderId = id + 'header';
    return shown ? (
      <RenderInBody>
        <Dismissable
          id={id}
          dismissCallback={
            this.props.canClickAway ? this.dismissModal : () => {}
          }
        >
          <ModalContext.Provider
            value={{ onDismiss: this.dismissModal, modalHeaderId }}
          >
            <div
              className={classnames(
                'llui-Modal-wrapper',
                this.props.modalWrapperClassName,
                {
                  'ov-v': !!this.props.enableFloatingDismiss,
                },
              )}
            >
              <dialog
                open
                aria-labelledby={modalHeaderId}
                aria-modal="true"
                className={classnames('llui-Modal', className, {
                  'llui-Modal-dismissable': !!this.props.enableFloatingDismiss,
                })}
              >
                <div
                  role="button"
                  tabIndex={0}
                  aria-hidden="true"
                  onFocus={this.handleFocusWrap(true)}
                />
                <div className="llui-Modal-inner" ref={this.modalChildrenRef}>
                  {children}
                </div>
                {this.props.enableFloatingDismiss && (
                  <button
                    type="button"
                    tabIndex={0}
                    className="u-resetBtn ModalTakeover-closeButton"
                    onClick={this.dismissModal}
                    aria-label={t('common.close')}
                  >
                    <Icon name="remove" />
                  </button>
                )}
                <div
                  role="button"
                  tabIndex={0}
                  aria-hidden="true"
                  onFocus={this.handleFocusWrap(false)}
                />
              </dialog>
            </div>
          </ModalContext.Provider>
        </Dismissable>
      </RenderInBody>
    ) : null;
  }
}

Modal.propTypes = {
  enableFloatingDismiss: PropTypes.bool,
  canClickAway: PropTypes.bool,
};

Modal.defaultProps = {
  enableFloatingDismiss: false,
  canClickAway: true,
};

export default Modal;
